"use client";

import { useState } from "react";
import { postJson } from "@lib/http";
import { useRouter } from "next/navigation";

import { useTranslations } from "@i18n/provider";
import { useOptionalAgentOverlayContext } from "./AgentOverlayProvider";
import { captureScreenshot } from "./captureScreenshot";
import { NAVIGATE_DESTINATIONS, type AgentAction } from "@lib/agent/actionToken";
import { lessonTypeAccent } from "@lib/learner/hubTints";
import { modalityToLessonType } from "@lib/learner/modality/modality";
import { GhostButton, PrimaryButton } from "@ui/buttons";
import { patchPreferences } from "@lib/userPreferencesClient";
import { modalityNounKey } from "@lib/learner/modality/labels";

type ChipStatus = "pending" | "busy" | "confirmed" | "failed" | "dismissed";

/**
 * Confirm chip for a tutor-proposed action (D-13). The D-14 guarantee is
 * structural: the ONLY side effect lives inside the "Do it" tap handler -
 * the chip never auto-fires, and its fetches hit the same authenticated
 * routes the on-page controls use. "Not now" dismisses silently.
 */
export function AgentActionChip({ action }: { action: AgentAction }) {
  const { t } = useTranslations();
  const router = useRouter();
  const overlay = useOptionalAgentOverlayContext();
  const [status, setStatus] = useState<ChipStatus>("pending");

  if (status === "dismissed") {
    return null;
  }

  const isBoardAction =
    action.type === "regenerate_slot" || action.type === "create_subject_lesson";

  const proposal = (() => {
    switch (action.type) {
      case "regenerate_slot":
        return t("agent.actionChip.regenerate", {
          modality: t(modalityNounKey(action.modality)),
        });
      case "create_subject_lesson":
        return t("agent.actionChip.request", {
          modality: t(modalityNounKey(action.modality)),
          subject: action.subject,
        });
      case "navigate":
        return t("agent.actionChip.navigate", {
          destination: t(`agent.actionChip.dest.${action.destination}`),
        });
      case "open_lesson":
        return t("agent.actionChip.openLesson", {
          title: action.title ?? t("agent.actionChip.openLessonFallbackTitle"),
        });
      case "set_preference":
        return t("agent.actionChip.setPreference", {
          key: action.key,
          value: String(action.value),
        });
      case "report_issue":
        return t("agent.actionChip.reportIssue", { summary: action.summary });
    }
  })();

  const doneMessage = (() => {
    switch (action.type) {
      case "regenerate_slot":
      case "create_subject_lesson":
        return t("agent.actionChip.done");
      case "set_preference":
        return t("agent.actionChip.donePreference");
      case "report_issue":
        return t("agent.actionChip.doneReport");
      default:
        return t("agent.actionChip.doneGeneric");
    }
  })();

  const failedMessage = isBoardAction
    ? t("agent.actionChip.failed")
    : t("agent.actionChip.failedGeneric");

  const execute = async () => {
    setStatus("busy");
    try {
      switch (action.type) {
        case "regenerate_slot": {
          const res = await postJson("/api/curriculum/board/regenerate", {
            lessonType: modalityToLessonType(action.modality),
          });
          setStatus(res.ok ? "confirmed" : "failed");
          break;
        }
        case "create_subject_lesson": {
          const res = await postJson("/api/curriculum/board/request", {
            subject: action.subject,
            lessonType: modalityToLessonType(action.modality),
          });
          setStatus(res.ok ? "confirmed" : "failed");
          break;
        }
        case "navigate": {
          setStatus("confirmed");
          router.push(NAVIGATE_DESTINATIONS[action.destination]);
          break;
        }
        case "open_lesson": {
          setStatus("confirmed");
          router.push(action.href);
          break;
        }
        case "set_preference": {
          const res = await patchPreferences({ [action.key]: action.value });
          setStatus(res.ok ? "confirmed" : "failed");
          if (res.ok) router.refresh();
          break;
        }
        case "report_issue": {
          const screenshot = await captureScreenshot();
          const res = await postJson("/api/feedback/report", {
            summary: action.summary,
            path: window.location.pathname,
            page: overlay?.getPromptPayload().pageContext?.page,
            ...(screenshot ? { screenshot } : {}),
          });
          setStatus(res.ok ? "confirmed" : "failed");
          break;
        }
      }
    } catch {
      setStatus("failed");
    }
  };

  const accent = isBoardAction ? lessonTypeAccent(modalityToLessonType(action.modality)) : "";

  return (
    <div
      aria-busy={status === "busy" ? true : undefined}
      className={`surface-card mt-1 max-w-[85%] rounded-[20px] p-4 ${accent}`}
    >
      {status === "confirmed" ? (
        <p className="flex items-center gap-2 text-sm text-ink-soft">
          <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
            check_circle
          </span>
          {doneMessage}
        </p>
      ) : status === "failed" ? (
        <p className="text-sm text-ink-soft">{failedMessage}</p>
      ) : (
        <>
          <p className="text-base text-ink-body">{proposal}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <PrimaryButton
              onClick={() => void execute()}
              disabled={status === "busy"}
              className="btn-compact"
            >
              {t("agent.actionChip.doIt")}
            </PrimaryButton>
            <GhostButton
              onClick={() => setStatus("dismissed")}
              disabled={status === "busy"}
              className="btn-compact"
            >
              {t("agent.actionChip.notNow")}
            </GhostButton>
          </div>
        </>
      )}
    </div>
  );
}
